<template>
  <transition name="contact-modal">
    <div v-if="open" class="contact-modal">
      <button
        class="contact-modal__backdrop"
        type="button"
        aria-label="Kontaktformular schliessen"
        @click="$emit('close')"
      ></button>

      <section class="contact-modal__panel">
        <div class="contact-modal__top">
          <div>
            <p class="section-kicker">Kontakt</p>
            <h2>Lass uns zusammen arbeiten.</h2>
            <p>
              Für Shootings, Events, Hochzeiten, Portraits, Sport, Content oder eine Idee,
              die noch keinen Namen hat.
            </p>
          </div>

          <button
            class="contact-modal__close"
            type="button"
            aria-label="Kontaktformular schliessen"
            @click="$emit('close')"
          >
            ×
          </button>
        </div>

        <form
          class="contact-form"
          action="https://formspree.io/f/mldjwjyv"
          method="POST"
          @submit.prevent="handleSubmit"
        >
          <div class="form-grid">
            <div class="form-group">
              <label for="contact-name">Name</label>
              <input
                id="contact-name"
                v-model="formData.name"
                type="text"
                name="name"
                :class="{ error: errors.name }"
                autocomplete="name"
              >
              <span v-if="errors.name" class="field-error">{{ errors.name }}</span>
            </div>

            <div class="form-group">
              <label for="contact-email">Email</label>
              <input
                id="contact-email"
                v-model="formData.email"
                type="email"
                name="email"
                :class="{ error: errors.email }"
                autocomplete="email"
              >
              <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="contact-subject">Betreff</label>
            <input
              id="contact-subject"
              v-model="formData.subject"
              type="text"
              name="subject"
              :class="{ error: errors.subject }"
            >
            <span v-if="errors.subject" class="field-error">{{ errors.subject }}</span>
          </div>

          <div class="form-group">
            <label for="contact-message">Nachricht</label>
            <textarea
              id="contact-message"
              v-model="formData.message"
              name="message"
              :class="{ error: errors.message }"
            ></textarea>
            <span v-if="errors.message" class="field-error">{{ errors.message }}</span>
          </div>

          <button
            type="submit"
            class="contact-submit"
            :disabled="isSubmitting"
          >
            <span>{{ isSubmitting ? 'Nachricht unterwegs...' : 'Nachricht senden' }}</span>
          </button>

          <transition name="form-message">
            <div v-if="submitSuccess" class="form-status form-status--success">
              Vielen Dank! Deine Nachricht wurde erfolgreich gesendet.
            </div>
          </transition>

          <transition name="form-message">
            <div v-if="submitError" class="form-status form-status--error">
              Ein Fehler ist aufgetreten. Bitte versuche es erneut oder schreibe mir direkt per Mail.
            </div>
          </transition>
        </form>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ContactModal',

  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  data() {
    return {
      formData: {
        name: '',
        email: '',
        subject: '',
        message: '',
      },
      errors: {},
      isSubmitting: false,
      submitSuccess: false,
      submitError: false,
    }
  },

  watch: {
    open(value) {
      if (value) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
  },

  beforeUnmount() {
    document.body.style.overflow = ''
  },

  methods: {
    validateForm() {
      this.errors = {}

      if (!this.formData.name.trim()) {
        this.errors.name = 'Name ist erforderlich'
      }

      if (!this.formData.email.trim()) {
        this.errors.email = 'Email ist erforderlich'
      } else if (!this.validateEmail(this.formData.email)) {
        this.errors.email = 'Bitte gib eine gültige Email-Adresse ein'
      }

      if (!this.formData.subject.trim()) {
        this.errors.subject = 'Betreff ist erforderlich'
      }

      if (!this.formData.message.trim()) {
        this.errors.message = 'Nachricht ist erforderlich'
      }

      return Object.keys(this.errors).length === 0
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },

    async handleSubmit(event) {
      if (!this.validateForm()) return

      this.isSubmitting = true
      this.submitError = false
      this.submitSuccess = false

      try {
        const form = event.target
        const formData = new FormData(form)

        const response = await fetch(form.action, {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        })

        if (!response.ok) {
          throw new Error('Network response was not ok')
        }

        this.submitSuccess = true

        this.formData = {
          name: '',
          email: '',
          subject: '',
          message: '',
        }

        window.setTimeout(() => {
          this.submitSuccess = false
          this.$emit('close')
        }, 1800)
      } catch (error) {
        console.error('Contact form error:', error)
        this.submitError = true
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.contact-modal {
  --background: #2f2e2e;
  --secondary-background: #454545;
  --accent: #8b7054;
  --accent-secondary: #6c553d;
  --text-primary: #d6d1ce;
  --text-secondary: #e6e1de;

  position: fixed;
  inset: 0;
  z-index: 600;
  display: grid;
  place-items: center;
  padding: 24px;
  color: var(--text-primary);
  font-family: 'Halenoir', sans-serif;
}

.contact-modal__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(12, 12, 12, 0.72);
  backdrop-filter: blur(18px);
  cursor: pointer;
}

.contact-modal__panel {
  position: relative;
  z-index: 1;
  width: min(100%, 860px);
  max-height: min(90vh, 920px);
  overflow: auto;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 36px;
  background:
    radial-gradient(circle at 20% 0%, rgba(139, 112, 84, 0.18), transparent 34%),
    linear-gradient(180deg, #2f2e2e 0%, #373636 100%);
  box-shadow: 0 30px 120px rgba(0, 0, 0, 0.42);
  padding: 42px;
}

.contact-modal__top {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 32px;
  align-items: start;
  margin-bottom: 34px;
}

.section-kicker {
  color: var(--accent);
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.28em;
  margin: 0 0 16px;
  text-transform: uppercase;
}

.contact-modal__top h2 {
  max-width: 680px;
  color: var(--text-secondary);
  font-size: clamp(3.2rem, 7vw, 6.6rem);
  font-weight: 900;
  letter-spacing: -0.07em;
  line-height: 0.9;
  margin: 0;
}

.contact-modal__top p:not(.section-kicker) {
  max-width: 620px;
  color: rgba(214, 209, 206, 0.82);
  font-size: 1.06rem;
  line-height: 1.75;
  margin: 24px 0 0;
}

.contact-modal__close {
  width: 48px;
  height: 48px;
  border: 1px solid rgba(214, 209, 206, 0.14);
  border-radius: 999px;
  background: rgba(230, 225, 222, 0.08);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 2rem;
  line-height: 1;
  transition:
    transform 180ms ease,
    background 180ms ease,
    color 180ms ease;
}

.contact-modal__close:hover {
  transform: rotate(8deg) scale(1.04);
  background: var(--accent);
  color: var(--background);
}

.contact-form {
  display: grid;
  gap: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.form-group {
  display: grid;
  gap: 9px;
}

.form-group label {
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.form-group input,
.form-group textarea {
  width: 100%;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 18px;
  background: rgba(69, 69, 69, 0.42);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 1rem;
  outline: none;
  padding: 16px 18px;
  transition:
    border-color 180ms ease,
    background 180ms ease,
    box-shadow 180ms ease;
}

.form-group textarea {
  min-height: 170px;
  resize: vertical;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: rgba(139, 112, 84, 0.72);
  background: rgba(69, 69, 69, 0.58);
  box-shadow: 0 0 0 4px rgba(139, 112, 84, 0.12);
}

.form-group input.error,
.form-group textarea.error {
  border-color: rgba(255, 95, 95, 0.75);
}

.field-error {
  color: #ff8d8d;
  font-size: 0.82rem;
  font-weight: 700;
}

.contact-submit {
  min-height: 56px;
  border: 0;
  border-radius: 999px;
  background: var(--accent);
  color: var(--background);
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 900;
  margin-top: 4px;
  padding: 0 28px;
  transition:
    transform 180ms ease,
    background 180ms ease,
    color 180ms ease,
    opacity 180ms ease;
}

.contact-submit:hover {
  transform: translateY(-2px);
  background: var(--accent-secondary);
  color: var(--text-secondary);
}

.contact-submit:disabled {
  cursor: not-allowed;
  opacity: 0.62;
  transform: none;
}

.form-status {
  border-radius: 18px;
  font-weight: 800;
  line-height: 1.5;
  padding: 16px 18px;
}

.form-status--success {
  border: 1px solid rgba(120, 220, 160, 0.28);
  background: rgba(120, 220, 160, 0.12);
  color: #b8f5cc;
}

.form-status--error {
  border: 1px solid rgba(255, 110, 110, 0.28);
  background: rgba(255, 110, 110, 0.12);
  color: #ffb4b4;
}

.contact-modal-enter-active,
.contact-modal-leave-active {
  transition: opacity 240ms ease;
}

.contact-modal-enter-active .contact-modal__panel,
.contact-modal-leave-active .contact-modal__panel {
  transition:
    opacity 260ms ease,
    transform 260ms ease;
}

.contact-modal-enter-from,
.contact-modal-leave-to {
  opacity: 0;
}

.contact-modal-enter-from .contact-modal__panel,
.contact-modal-leave-to .contact-modal__panel {
  opacity: 0;
  transform: translateY(18px) scale(0.985);
}

.form-message-enter-active,
.form-message-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease;
}

.form-message-enter-from,
.form-message-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 720px) {
  .contact-modal {
    padding: 12px;
  }

  .contact-modal__panel {
    max-height: 92vh;
    border-radius: 28px;
    padding: 26px;
  }

  .contact-modal__top,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .contact-modal__top h2 {
    font-size: clamp(3rem, 15vw, 5rem);
  }
}
</style>